import { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Copy, Check, BookOpen, Key, ShoppingBag, ShoppingCart, Users, Store, Webhook, Zap, ChevronRight } from 'lucide-react';

const BASE_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// ─── Code Snippet Copy Button ───────────────────────────────────────────────
function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <button onClick={copy} className="absolute top-3 right-3 p-1.5 rounded bg-white/10 hover:bg-white/20 transition-colors text-slate-400 hover:text-white">
      {copied ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

// ─── Code Block ─────────────────────────────────────────────────────────────
function CodeBlock({ code, lang = '' }) {
  return (
    <div className="relative">
      <CopyBtn text={code} />
      <pre className="bg-[#0d1117] text-[#c9d1d9] rounded-lg p-4 pr-12 overflow-x-auto text-sm font-mono leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  );
}

// ─── Method Badge ───────────────────────────────────────────────────────────
function MethodBadge({ method }) {
  const colors = {
    GET: 'bg-green-100 text-green-700 border-green-200',
    POST: 'bg-blue-100 text-blue-700 border-blue-200',
    PUT: 'bg-amber-100 text-amber-700 border-amber-200',
    DELETE: 'bg-red-100 text-red-700 border-red-200',
  };
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-bold border ${colors[method] || ''}`}>{method}</span>;
}

// ─── Code Tabs Component ────────────────────────────────────────────────────
function CodeTabs({ examples }) {
  return (
    <Tabs defaultValue="curl" className="mt-3">
      <TabsList className="bg-[#161b22] border border-[#30363d] h-8">
        {Object.keys(examples).map(lang => (
          <TabsTrigger key={lang} value={lang} className="text-xs px-3 py-1 data-[state=active]:bg-[#0d1117] data-[state=active]:text-white text-slate-400">{lang}</TabsTrigger>
        ))}
      </TabsList>
      {Object.entries(examples).map(([lang, code]) => (
        <TabsContent key={lang} value={lang} className="mt-2"><CodeBlock code={code.trim()} lang={lang} /></TabsContent>
      ))}
    </Tabs>
  );
}

// ─── Param Table ────────────────────────────────────────────────────────────
function ParamTable({ params }) {
  return (
    <div className="border rounded-lg overflow-hidden mt-3">
      <table className="w-full text-sm">
        <thead><tr className="bg-slate-50 border-b"><th className="text-left px-4 py-2 font-medium">Parameter</th><th className="text-left px-4 py-2 font-medium">Type</th><th className="text-left px-4 py-2 font-medium">Required</th><th className="text-left px-4 py-2 font-medium">Description</th></tr></thead>
        <tbody>
          {params.map((p, i) => (
            <tr key={i} className="border-b last:border-0">
              <td className="px-4 py-2 font-mono text-xs">{p.name}</td>
              <td className="px-4 py-2"><Badge variant="outline" className="text-xs">{p.type}</Badge></td>
              <td className="px-4 py-2 text-xs">{p.required ? <span className="text-red-500">Yes</span> : <span className="text-muted-foreground">No</span>}</td>
              <td className="px-4 py-2 text-xs text-muted-foreground">{p.desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Sections Data ──────────────────────────────────────────────────────────
const NAV_SECTIONS = [
  { id: 'getting-started', label: 'Getting Started', icon: BookOpen },
  { id: 'authentication', label: 'Authentication', icon: Key },
  { id: 'products', label: 'Products', icon: ShoppingBag },
  { id: 'orders', label: 'Orders', icon: ShoppingCart },
  { id: 'customers', label: 'Customers', icon: Users },
  { id: 'store', label: 'Store', icon: Store },
  { id: 'webhooks', label: 'Webhooks', icon: Webhook },
  { id: 'events', label: 'Event Reference', icon: Zap },
];

const EVENTS_TABLE = [
  { event: 'order.created', desc: 'Pesanan baru dibuat' },
  { event: 'order.paid', desc: 'Pembayaran pesanan dikonfirmasi' },
  { event: 'order.shipped', desc: 'Pesanan dikirim' },
  { event: 'order.delivered', desc: 'Pesanan diterima' },
  { event: 'order.canceled', desc: 'Pesanan dibatalkan' },
  { event: 'payment.success', desc: 'Pembayaran berhasil' },
  { event: 'payment.failed', desc: 'Pembayaran gagal' },
  { event: 'customer.created', desc: 'Customer baru terdaftar' },
  { event: 'product.stock_low', desc: 'Stok produk menipis' },
  { event: 'membership.activated', desc: 'Membership diaktifkan' },
  { event: 'course.enrolled', desc: 'Customer mendaftar kursus' },
  { event: 'donation.received', desc: 'Donasi diterima' },
  { event: 'cart.abandoned', desc: 'Keranjang ditinggalkan' },
];

// ─── Main Component ─────────────────────────────────────────────────────────
export default function APIDocs() {
  const [activeSection, setActiveSection] = useState('getting-started');
  const contentRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter(e => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActiveSection(visible[0].target.id);
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0.1 }
    );
    NAV_SECTIONS.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="flex gap-6 p-6 max-w-7xl mx-auto">
      {/* ─── Sidebar Navigation ─── */}
      <nav className="hidden lg:block w-56 shrink-0">
        <div className="sticky top-6 space-y-1">
          <h2 className="text-sm font-semibold text-muted-foreground mb-3 px-3">API DOCUMENTATION</h2>
          {NAV_SECTIONS.map(s => {
            const Icon = s.icon;
            return (
              <button key={s.id} onClick={() => scrollTo(s.id)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${activeSection === s.id ? 'bg-primary text-primary-foreground font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-slate-100'}`}>
                <Icon className="h-4 w-4" />{s.label}
              </button>
            );
          })}
        </div>
      </nav>

      {/* ─── Main Content ─── */}
      <div className="flex-1 min-w-0 space-y-12" ref={contentRef}>

        {/* ═══ GETTING STARTED ════════════════════════════════════════════ */}
        <section id="getting-started">
          <div className="border-b pb-4 mb-6">
            <h1 className="text-3xl font-bold">Tokomoe REST API</h1>
            <p className="text-lg text-muted-foreground mt-2">Integrasi toko online Anda dengan sistem eksternal menggunakan REST API dan Webhooks</p>
          </div>

          <Card>
            <CardContent className="pt-6 space-y-4">
              <h3 className="text-lg font-semibold">Base URL</h3>
              <CodeBlock code={`${BASE_URL}/v1`} />

              <h3 className="text-lg font-semibold mt-6">Quick Start</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="h-7 w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shrink-0">1</div>
                  <div>
                    <p className="font-medium">Generate API Key</p>
                    <p className="text-sm text-muted-foreground">Buka menu <strong>API Keys</strong> di dashboard dan generate key baru</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="h-7 w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shrink-0">2</div>
                  <div>
                    <p className="font-medium">Tambahkan Header Authentication</p>
                    <p className="text-sm text-muted-foreground">Sertakan <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">X-API-Key: sk_live_xxx...</code> di setiap request</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="h-7 w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shrink-0">3</div>
                  <div>
                    <p className="font-medium">Mulai Request</p>
                    <p className="text-sm text-muted-foreground">Kirim request ke endpoint yang tersedia dan proses response JSON</p>
                  </div>
                </div>
              </div>

              <h3 className="text-lg font-semibold mt-6">Response Format</h3>
              <p className="text-sm text-muted-foreground">Semua response menggunakan format JSON. Paginated endpoints mengembalikan <code className="bg-slate-100 px-1 rounded text-xs">total</code>, <code className="bg-slate-100 px-1 rounded text-xs">page</code>, dan <code className="bg-slate-100 px-1 rounded text-xs">page_size</code>.</p>
              <CodeBlock code={`{
  "products": [...],
  "total": 42,
  "page": 1,
  "page_size": 20
}`} />

              <h3 className="text-lg font-semibold mt-6">Error Handling</h3>
              <CodeBlock code={`// HTTP 4xx/5xx responses
{
  "detail": "Error message describing what went wrong"
}`} />
            </CardContent>
          </Card>
        </section>

        {/* ═══ AUTHENTICATION ═════════════════════════════════════════════ */}
        <section id="authentication">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2"><Key className="h-6 w-6" />Authentication</h2>

          <Card>
            <CardContent className="pt-6 space-y-4">
              <p className="text-muted-foreground">Semua request ke API v1 memerlukan header <code className="bg-slate-100 px-1.5 rounded font-mono text-sm">X-API-Key</code> yang berisi API key Anda.</p>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800"><strong>⚠️ Penting:</strong> Jangan pernah menyertakan API key di client-side code (frontend/mobile app). Gunakan hanya di server-side (backend).</p>
              </div>

              <CodeTabs examples={{
                'cURL': `curl -X GET "${BASE_URL}/v1/products" \\
  -H "X-API-Key: sk_live_your_api_key_here"`,
                'Python': `import requests

API_KEY = "sk_live_your_api_key_here"
BASE = "${BASE_URL}/v1"

response = requests.get(
    f"{BASE}/products",
    headers={"X-API-Key": API_KEY}
)
data = response.json()
print(f"Total products: {data['total']}")`,
                'Node.js': `const axios = require('axios');

const API_KEY = 'sk_live_your_api_key_here';
const BASE = '${BASE_URL}/v1';

const { data } = await axios.get(\`\${BASE}/products\`, {
  headers: { 'X-API-Key': API_KEY }
});
console.log(\`Total products: \${data.total}\`);`,
                'PHP': `<?php
$apiKey = 'sk_live_your_api_key_here';
$base = '${BASE_URL}/v1';

$ch = curl_init("$base/products");
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => [
        "X-API-Key: $apiKey"
    ],
]);
$response = json_decode(curl_exec($ch), true);
curl_close($ch);
echo "Total products: " . $response['total'];`
              }} />
            </CardContent>
          </Card>
        </section>

        {/* ═══ PRODUCTS ═══════════════════════════════════════════════════ */}
        <section id="products">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2"><ShoppingBag className="h-6 w-6" />Products</h2>

          {/* List Products */}
          <Card className="mb-4">
            <CardContent className="pt-6 space-y-3">
              <div className="flex items-center gap-3">
                <MethodBadge method="GET" />
                <code className="text-sm font-mono font-medium">/v1/products</code>
              </div>
              <p className="text-sm text-muted-foreground">Ambil daftar produk toko Anda dengan pagination dan pencarian.</p>
              <ParamTable params={[
                { name: 'page', type: 'integer', required: false, desc: 'Nomor halaman (default: 1)' },
                { name: 'page_size', type: 'integer', required: false, desc: 'Jumlah per halaman, max 100 (default: 20)' },
                { name: 'search', type: 'string', required: false, desc: 'Cari berdasarkan nama produk' },
              ]} />
              <CodeTabs examples={{
                'cURL': `curl "${BASE_URL}/v1/products?page=1&page_size=10&search=kaos" \\
  -H "X-API-Key: sk_live_your_key"`,
                'Python': `response = requests.get(
    f"{BASE}/products",
    headers={"X-API-Key": API_KEY},
    params={"page": 1, "page_size": 10, "search": "kaos"}
)
products = response.json()["products"]`,
                'Node.js': `const { data } = await axios.get(\`\${BASE}/products\`, {
  headers: { 'X-API-Key': API_KEY },
  params: { page: 1, page_size: 10, search: 'kaos' }
});`,
                'PHP': `$ch = curl_init("$base/products?" . http_build_query([
    'page' => 1, 'page_size' => 10, 'search' => 'kaos'
]));
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => ["X-API-Key: $apiKey"],
]);
$products = json_decode(curl_exec($ch), true);`
              }} />
              <p className="text-xs font-medium text-muted-foreground mt-4">Response:</p>
              <CodeBlock code={`{
  "products": [
    {
      "id": "abc-123",
      "name": "Kaos Premium",
      "price": 150000,
      "stock": 25,
      "type": "physical",
      "images": ["https://..."],
      "created_at": "2026-01-15T10:30:00Z"
    }
  ],
  "total": 42,
  "page": 1,
  "page_size": 10
}`} />
            </CardContent>
          </Card>

          {/* Get Product */}
          <Card>
            <CardContent className="pt-6 space-y-3">
              <div className="flex items-center gap-3">
                <MethodBadge method="GET" />
                <code className="text-sm font-mono font-medium">/v1/products/{'{product_id}'}</code>
              </div>
              <p className="text-sm text-muted-foreground">Ambil detail satu produk berdasarkan ID.</p>
              <CodeTabs examples={{
                'cURL': `curl "${BASE_URL}/v1/products/abc-123" \\
  -H "X-API-Key: sk_live_your_key"`,
                'Python': `product = requests.get(
    f"{BASE}/products/abc-123",
    headers={"X-API-Key": API_KEY}
).json()`,
                'Node.js': `const { data: product } = await axios.get(
  \`\${BASE}/products/abc-123\`,
  { headers: { 'X-API-Key': API_KEY } }
);`,
                'PHP': `$ch = curl_init("$base/products/abc-123");
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => ["X-API-Key: $apiKey"],
]);
$product = json_decode(curl_exec($ch), true);`
              }} />
            </CardContent>
          </Card>
        </section>

        {/* ═══ ORDERS ═════════════════════════════════════════════════════ */}
        <section id="orders">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2"><ShoppingCart className="h-6 w-6" />Orders</h2>

          {/* List Orders */}
          <Card className="mb-4">
            <CardContent className="pt-6 space-y-3">
              <div className="flex items-center gap-3">
                <MethodBadge method="GET" />
                <code className="text-sm font-mono font-medium">/v1/orders</code>
              </div>
              <p className="text-sm text-muted-foreground">Ambil daftar pesanan dengan filter status dan tanggal.</p>
              <ParamTable params={[
                { name: 'status', type: 'string', required: false, desc: 'Filter status: pending, paid, shipped, delivered, canceled' },
                { name: 'start_date', type: 'string', required: false, desc: 'Filter tanggal mulai (ISO format)' },
                { name: 'end_date', type: 'string', required: false, desc: 'Filter tanggal akhir (ISO format)' },
                { name: 'page', type: 'integer', required: false, desc: 'Nomor halaman (default: 1)' },
                { name: 'page_size', type: 'integer', required: false, desc: 'Jumlah per halaman (default: 20)' },
              ]} />
              <CodeTabs examples={{
                'cURL': `curl "${BASE_URL}/v1/orders?status=paid&page=1" \\
  -H "X-API-Key: sk_live_your_key"`,
                'Python': `response = requests.get(
    f"{BASE}/orders",
    headers={"X-API-Key": API_KEY},
    params={"status": "paid", "page": 1}
)
orders = response.json()["orders"]`,
                'Node.js': `const { data } = await axios.get(\`\${BASE}/orders\`, {
  headers: { 'X-API-Key': API_KEY },
  params: { status: 'paid', page: 1 }
});`,
                'PHP': `$ch = curl_init("$base/orders?" . http_build_query([
    'status' => 'paid', 'page' => 1
]));
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => ["X-API-Key: $apiKey"],
]);
$orders = json_decode(curl_exec($ch), true);`
              }} />
              <p className="text-xs font-medium text-muted-foreground mt-4">Response:</p>
              <CodeBlock code={`{
  "orders": [
    {
      "id": "ord-456",
      "customer_name": "John Doe",
      "status": "paid",
      "total": 300000,
      "items": [...],
      "created_at": "2026-03-01T08:15:00Z"
    }
  ],
  "total": 128,
  "page": 1,
  "page_size": 20
}`} />
            </CardContent>
          </Card>

          {/* Get Order */}
          <Card className="mb-4">
            <CardContent className="pt-6 space-y-3">
              <div className="flex items-center gap-3">
                <MethodBadge method="GET" />
                <code className="text-sm font-mono font-medium">/v1/orders/{'{order_id}'}</code>
              </div>
              <p className="text-sm text-muted-foreground">Ambil detail satu pesanan.</p>
              <CodeTabs examples={{
                'cURL': `curl "${BASE_URL}/v1/orders/ord-456" \\
  -H "X-API-Key: sk_live_your_key"`,
                'Python': `order = requests.get(
    f"{BASE}/orders/ord-456",
    headers={"X-API-Key": API_KEY}
).json()`,
                'Node.js': `const { data: order } = await axios.get(
  \`\${BASE}/orders/ord-456\`,
  { headers: { 'X-API-Key': API_KEY } }
);`,
                'PHP': `$ch = curl_init("$base/orders/ord-456");
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => ["X-API-Key: $apiKey"],
]);
$order = json_decode(curl_exec($ch), true);`
              }} />
            </CardContent>
          </Card>

          {/* Update Order Status */}
          <Card>
            <CardContent className="pt-6 space-y-3">
              <div className="flex items-center gap-3">
                <MethodBadge method="POST" />
                <code className="text-sm font-mono font-medium">/v1/orders/{'{order_id}'}/status</code>
              </div>
              <p className="text-sm text-muted-foreground">Update status pesanan. Akan otomatis trigger webhook event terkait.</p>
              <ParamTable params={[
                { name: 'status', type: 'string', required: true, desc: 'Status baru: pending, paid, processing, shipped, delivered, canceled' },
              ]} />
              <CodeTabs examples={{
                'cURL': `curl -X POST "${BASE_URL}/v1/orders/ord-456/status" \\
  -H "X-API-Key: sk_live_your_key" \\
  -H "Content-Type: application/json" \\
  -d '{"status": "shipped"}'`,
                'Python': `response = requests.post(
    f"{BASE}/orders/ord-456/status",
    headers={"X-API-Key": API_KEY},
    json={"status": "shipped"}
)
print(response.json())
# => {"order_id": "ord-456", "status": "shipped", "updated_at": "..."}`,
                'Node.js': `const { data } = await axios.post(
  \`\${BASE}/orders/ord-456/status\`,
  { status: 'shipped' },
  { headers: { 'X-API-Key': API_KEY } }
);`,
                'PHP': `$ch = curl_init("$base/orders/ord-456/status");
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_POST => true,
    CURLOPT_HTTPHEADER => [
        "X-API-Key: $apiKey",
        "Content-Type: application/json"
    ],
    CURLOPT_POSTFIELDS => json_encode(['status' => 'shipped']),
]);
$result = json_decode(curl_exec($ch), true);`
              }} />
            </CardContent>
          </Card>
        </section>

        {/* ═══ CUSTOMERS ══════════════════════════════════════════════════ */}
        <section id="customers">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2"><Users className="h-6 w-6" />Customers</h2>

          <Card>
            <CardContent className="pt-6 space-y-3">
              <div className="flex items-center gap-3">
                <MethodBadge method="GET" />
                <code className="text-sm font-mono font-medium">/v1/customers</code>
              </div>
              <p className="text-sm text-muted-foreground">Ambil daftar customer toko Anda dengan pencarian.</p>
              <ParamTable params={[
                { name: 'search', type: 'string', required: false, desc: 'Cari berdasarkan nama, email, atau phone' },
                { name: 'page', type: 'integer', required: false, desc: 'Nomor halaman (default: 1)' },
                { name: 'page_size', type: 'integer', required: false, desc: 'Jumlah per halaman (default: 20)' },
              ]} />
              <CodeTabs examples={{
                'cURL': `curl "${BASE_URL}/v1/customers?search=john&page=1" \\
  -H "X-API-Key: sk_live_your_key"`,
                'Python': `response = requests.get(
    f"{BASE}/customers",
    headers={"X-API-Key": API_KEY},
    params={"search": "john"}
)
customers = response.json()["customers"]`,
                'Node.js': `const { data } = await axios.get(\`\${BASE}/customers\`, {
  headers: { 'X-API-Key': API_KEY },
  params: { search: 'john' }
});`,
                'PHP': `$ch = curl_init("$base/customers?" . http_build_query([
    'search' => 'john'
]));
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => ["X-API-Key: $apiKey"],
]);
$customers = json_decode(curl_exec($ch), true);`
              }} />
            </CardContent>
          </Card>
        </section>

        {/* ═══ STORE ══════════════════════════════════════════════════════ */}
        <section id="store">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2"><Store className="h-6 w-6" />Store</h2>

          <Card>
            <CardContent className="pt-6 space-y-3">
              <div className="flex items-center gap-3">
                <MethodBadge method="GET" />
                <code className="text-sm font-mono font-medium">/v1/store/info</code>
              </div>
              <p className="text-sm text-muted-foreground">Ambil informasi toko Anda: nama, subdomain, pengaturan.</p>
              <CodeTabs examples={{
                'cURL': `curl "${BASE_URL}/v1/store/info" \\
  -H "X-API-Key: sk_live_your_key"`,
                'Python': `store = requests.get(
    f"{BASE}/store/info",
    headers={"X-API-Key": API_KEY}
).json()
print(f"Store: {store['name']}")`,
                'Node.js': `const { data: store } = await axios.get(
  \`\${BASE}/store/info\`,
  { headers: { 'X-API-Key': API_KEY } }
);
console.log(\`Store: \${store.name}\`);`,
                'PHP': `$ch = curl_init("$base/store/info");
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => ["X-API-Key: $apiKey"],
]);
$store = json_decode(curl_exec($ch), true);
echo "Store: " . $store['name'];`
              }} />
              <p className="text-xs font-medium text-muted-foreground mt-4">Response:</p>
              <CodeBlock code={`{
  "id": "d1d40e12-...",
  "name": "My Awesome Store",
  "subdomain": "mystore",
  "settings": {
    "currency": "IDR",
    "timezone": "Asia/Jakarta"
  }
}`} />
            </CardContent>
          </Card>
        </section>

        {/* ═══ WEBHOOKS ═══════════════════════════════════════════════════ */}
        <section id="webhooks">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2"><Webhook className="h-6 w-6" />Webhooks</h2>

          <Card className="mb-4">
            <CardContent className="pt-6 space-y-4">
              <h3 className="text-lg font-semibold">Cara Kerja Webhook</h3>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>Ketika event terjadi di toko Anda (misal: pesanan baru), Tokomoe akan mengirim HTTP POST request ke URL yang Anda daftarkan dengan payload JSON dan signature untuk verifikasi.</p>

                <div className="grid sm:grid-cols-3 gap-3 mt-4">
                  <div className="border rounded-lg p-4 text-center">
                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-2"><span className="text-blue-700 font-bold">1</span></div>
                    <p className="font-medium text-foreground">Event Terjadi</p>
                    <p className="text-xs mt-1">Order baru, pembayaran, dll</p>
                  </div>
                  <div className="border rounded-lg p-4 text-center">
                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-2"><span className="text-blue-700 font-bold">2</span></div>
                    <p className="font-medium text-foreground">Tokomoe POST</p>
                    <p className="text-xs mt-1">Kirim payload + HMAC signature</p>
                  </div>
                  <div className="border rounded-lg p-4 text-center">
                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-2"><span className="text-blue-700 font-bold">3</span></div>
                    <p className="font-medium text-foreground">Server Anda</p>
                    <p className="text-xs mt-1">Verifikasi signature & proses</p>
                  </div>
                </div>
              </div>

              <h3 className="text-lg font-semibold mt-6">Webhook Payload Structure</h3>
              <CodeBlock code={`{
  "event": "order.paid",
  "tenant_id": "d1d40e12-...",
  "data": {
    "order_id": "ord-456",
    "status": "paid",
    "total": 300000,
    "customer_name": "John Doe"
  },
  "timestamp": "2026-03-09T10:30:00Z"
}`} />

              <h3 className="text-lg font-semibold mt-6">Headers yang Dikirim</h3>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="bg-slate-50 border-b"><th className="text-left px-4 py-2">Header</th><th className="text-left px-4 py-2">Description</th></tr></thead>
                  <tbody>
                    <tr className="border-b"><td className="px-4 py-2 font-mono text-xs">Content-Type</td><td className="px-4 py-2 text-xs">application/json</td></tr>
                    <tr className="border-b"><td className="px-4 py-2 font-mono text-xs">X-Tokomoe-Signature</td><td className="px-4 py-2 text-xs">HMAC-SHA256 signature: sha256=...</td></tr>
                    <tr><td className="px-4 py-2 font-mono text-xs">X-Tokomoe-Event</td><td className="px-4 py-2 text-xs">Nama event, misal: order.paid</td></tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-lg font-semibold mt-6">Verifikasi Signature</h3>
              <p className="text-sm text-muted-foreground">Selalu verifikasi signature webhook untuk memastikan request benar-benar dari Tokomoe. Gunakan webhook secret Anda untuk validasi HMAC-SHA256.</p>

              <CodeTabs examples={{
                'Python': `import hmac
import hashlib

def verify_webhook(payload_body: bytes, signature: str, secret: str) -> bool:
    """Verify Tokomoe webhook signature"""
    expected = "sha256=" + hmac.new(
        secret.encode(),
        payload_body,
        hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(expected, signature)

# Usage in Flask:
@app.route('/webhooks/Tokomoe', methods=['POST'])
def handle_webhook():
    signature = request.headers.get('X-Tokomoe-Signature', '')
    if not verify_webhook(request.data, signature, WEBHOOK_SECRET):
        return 'Invalid signature', 403

    event = request.headers.get('X-Tokomoe-Event')
    data = request.json

    if event == 'order.paid':
        process_paid_order(data['data'])

    return 'OK', 200`,
                'Node.js': `const crypto = require('crypto');
const express = require('express');
const app = express();

app.use(express.raw({ type: 'application/json' }));

function verifyWebhook(body, signature, secret) {
  const expected = 'sha256=' + crypto
    .createHmac('sha256', secret)
    .update(body)
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(expected),
    Buffer.from(signature)
  );
}

app.post('/webhooks/Tokomoe', (req, res) => {
  const sig = req.headers['x-Tokomoe-signature'] || '';
  if (!verifyWebhook(req.body, sig, WEBHOOK_SECRET)) {
    return res.status(403).send('Invalid signature');
  }

  const event = req.headers['x-Tokomoe-event'];
  const data = JSON.parse(req.body);

  if (event === 'order.paid') {
    processPaidOrder(data.data);
  }

  res.status(200).send('OK');
});`,
                'PHP': `<?php
function verifyWebhook($payloadBody, $signature, $secret) {
    $expected = 'sha256=' . hash_hmac('sha256', $payloadBody, $secret);
    return hash_equals($expected, $signature);
}

// Usage:
$payload = file_get_contents('php://input');
$signature = $_SERVER['HTTP_X_Tokomoe_SIGNATURE'] ?? '';

if (!verifyWebhook($payload, $signature, $webhookSecret)) {
    http_response_code(403);
    echo 'Invalid signature';
    exit;
}

$event = $_SERVER['HTTP_X_Tokomoe_EVENT'] ?? '';
$data = json_decode($payload, true);

switch ($event) {
    case 'order.paid':
        processPaidOrder($data['data']);
        break;
    case 'order.shipped':
        processShippedOrder($data['data']);
        break;
}

http_response_code(200);
echo 'OK';`,
                'cURL': `# Test webhook verification manually:
SECRET="whsec_your_webhook_secret"
BODY='{"event":"order.paid","data":{"order_id":"ord-456"}}'
SIG="sha256=$(echo -n "$BODY" | openssl dgst -sha256 -hmac "$SECRET" | cut -d' ' -f2)"

curl -X POST "https://yourapp.com/webhooks/Tokomoe" \\
  -H "Content-Type: application/json" \\
  -H "X-Tokomoe-Signature: $SIG" \\
  -H "X-Tokomoe-Event: order.paid" \\
  -d "$BODY"`
              }} />

              <h3 className="text-lg font-semibold mt-6">Retry Policy</h3>
              <div className="bg-slate-50 border rounded-lg p-4 text-sm space-y-1">
                <p>• Tokomoe akan mencoba mengirim webhook hingga <strong>3 kali</strong> jika request gagal</p>
                <p>• Timeout per request: <strong>10 detik</strong></p>
                <p>• Response 2xx dianggap sukses, selain itu dianggap gagal</p>
                <p>• Delivery yang gagal bisa di-retry manual melalui dashboard</p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* ═══ EVENT REFERENCE ════════════════════════════════════════════ */}
        <section id="events">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2"><Zap className="h-6 w-6" />Event Reference</h2>

          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground mb-4">Daftar lengkap webhook events yang tersedia di Tokomoe. Pilih events yang relevan saat mendaftarkan webhook endpoint.</p>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b">
                      <th className="text-left px-4 py-3 font-medium">Event</th>
                      <th className="text-left px-4 py-3 font-medium">Description</th>
                      <th className="text-left px-4 py-3 font-medium">Example Trigger</th>
                    </tr>
                  </thead>
                  <tbody>
                    {EVENTS_TABLE.map((e, i) => (
                      <tr key={e.event} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="px-4 py-3"><code className="bg-slate-100 px-2 py-0.5 rounded text-xs font-mono">{e.event}</code></td>
                        <td className="px-4 py-3 text-muted-foreground">{e.desc}</td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">
                          {e.event === 'order.created' && 'Customer checkout'}
                          {e.event === 'order.paid' && 'Payment confirmed via gateway'}
                          {e.event === 'order.shipped' && 'Admin marks order as shipped'}
                          {e.event === 'order.delivered' && 'Delivery confirmed'}
                          {e.event === 'order.canceled' && 'Order canceled by admin/customer'}
                          {e.event === 'payment.success' && 'Payment gateway callback'}
                          {e.event === 'payment.failed' && 'Payment declined/expired'}
                          {e.event === 'customer.created' && 'New registration'}
                          {e.event === 'product.stock_low' && 'Stock below threshold'}
                          {e.event === 'membership.activated' && 'Membership payment confirmed'}
                          {e.event === 'course.enrolled' && 'Course enrollment completed'}
                          {e.event === 'donation.received' && 'Donation payment received'}
                          {e.event === 'cart.abandoned' && 'Cart inactive for 1+ hour'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h3 className="text-lg font-semibold mt-6">Example Event Payloads</h3>

              <h4 className="font-medium mt-4 text-sm">order.paid</h4>
              <CodeBlock code={`{
  "event": "order.paid",
  "tenant_id": "d1d40e12-...",
  "data": {
    "order_id": "ord-456",
    "status": "paid",
    "total": 300000,
    "payment_method": "bank_transfer",
    "customer_name": "John Doe",
    "customer_email": "john@example.com",
    "items": [
      {"product_id": "abc-123", "name": "Kaos Premium", "qty": 2, "price": 150000}
    ]
  },
  "timestamp": "2026-03-09T10:30:00Z"
}`} />

              <h4 className="font-medium mt-4 text-sm">product.stock_low</h4>
              <CodeBlock code={`{
  "event": "product.stock_low",
  "tenant_id": "d1d40e12-...",
  "data": {
    "product_id": "abc-123",
    "product_name": "Kaos Premium",
    "current_stock": 3,
    "threshold": 5
  },
  "timestamp": "2026-03-09T10:30:00Z"
}`} />

              <h4 className="font-medium mt-4 text-sm">cart.abandoned</h4>
              <CodeBlock code={`{
  "event": "cart.abandoned",
  "tenant_id": "d1d40e12-...",
  "data": {
    "cart_id": "cart-789",
    "customer_name": "Jane Doe",
    "customer_email": "jane@example.com",
    "items": [
      {"product_id": "abc-123", "name": "Kaos Premium", "qty": 1, "price": 150000}
    ],
    "total": 150000,
    "abandoned_since": "2026-03-09T09:30:00Z"
  },
  "timestamp": "2026-03-09T10:30:00Z"
}`} />
            </CardContent>
          </Card>
        </section>

        <div className="h-20" />
      </div>
    </div>
  );
}
